import { memo, useEffect } from 'react';
import { useSRZId } from '@/hooks';
import { Mainboard } from '@/pages/HomePage/OrganizationPage/Mainboard';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import { sortBy } from 'lodash';
import { PopupContextProvider, DetailsPanelProvider } from '@/contexts';
import { PageLayoutMain, PageLayoutRoot } from '@/components/PageLayout';
import { selectDirty } from '@/components/studio/ScopingEditor/reducer';
import { gql } from '@apollo/client';
import { useOrganizationPageFloorsLazyQuery } from '@/.generated/graphql';
import { useSelector } from 'react-redux';
import DashboardHeader from '@/pages/DashboardHeader';
import { SettingsPage } from '@/pages/Settings';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { NoSpacesAvailable } from '@/components/NoSpacesAvailable';
import { isDev } from '@/constants';

export const OrganizationPage = () => {
  const { spaceId, roomId, zoneId, buildingId } = useSRZId();
  const navigate = useNavigate();
  const location = useLocation();
  const [getFloors] = useOrganizationPageFloorsLazyQuery();
  const {
    isUnrestricted,
    hasAccess,
    isLoading: isAccessLoading,
    hasAnyAccess,
    getFirstAccessibleTarget,
  } = useSpaceAccess();

  const isDirty = useSelector(selectDirty);

  useEffect(() => {
    const onbeforeunload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    if (isDirty) {
      window.addEventListener('beforeunload', onbeforeunload);
    }

    return () => window.removeEventListener('beforeunload', onbeforeunload);
  }, [isDirty]);

  // Error handling, and redirect to first accessible space if no space is indicated by query param (& not on building page!)
  useEffect(() => {
    // Wait for access check to complete before redirecting
    if (isAccessLoading) {
      return;
    }

    const isSettingsRoute = location.pathname.startsWith('/settings');
    const isDevicesRoute = location.pathname.startsWith('/devices');
    if (!spaceId && !isSettingsRoute && !isDevicesRoute && !buildingId) {
      // Check if user has any access at all
      if (!hasAnyAccess()) {
        if (isDev)
          console.log('[OrganizationPage] User has no access to any spaces, not redirecting');
        return;
      }

      // Space id is not defined in url, redirect to first accessible floor
      getFloors().then((res) => {
        const allFloors = res.data?.floors?.data ?? [];

        // Filter floors based on user access
        const accessibleFloors = isUnrestricted
          ? allFloors
          : allFloors.filter((floor) => hasAccess('floor', floor.id));

        if (isDev) {
          console.log(
            `[OrganizationPage] Total floors: ${allFloors.length}, Accessible: ${accessibleFloors.length}`,
          );
        }

        const floor = sortBy(accessibleFloors, 'name')[0]?.id;
        if (floor) {
          if (isDev) console.log(`[OrganizationPage] Redirecting to accessible floor: ${floor}`);
          const params = new URLSearchParams({ spaceId: floor });
          navigate({ search: `?${params.toString()}` });
          return;
        }

        // No directly-accessible floors. A user whose only grant is a room
        // or zone still has a valid navigation target — the parent floor of
        // that leaf. Preserve the leaf in the URL so the Dashboard renders
        // the room/zone view, not the floor aggregates (which the user
        // isn't authorized to see).
        const target = getFirstAccessibleTarget();
        if (target) {
          const params = new URLSearchParams({ spaceId: target.floorId });
          if (target.roomId) params.set('roomId', target.roomId);
          if (target.zoneId) params.set('zoneId', target.zoneId);
          const search = `?${params.toString()}`;
          if (isDev) console.log(`[OrganizationPage] Redirecting to accessible leaf: ${search}`);
          navigate({ search });
          return;
        }

        if (isDev) console.log('[OrganizationPage] No accessible floors or leaves found');
      });
    }
  }, [
    spaceId,
    navigate,
    getFloors,
    location.pathname,
    buildingId,
    isAccessLoading,
    isUnrestricted,
    hasAccess,
    hasAnyAccess,
    getFirstAccessibleTarget,
  ]);

  const isDevicesRoute = location.pathname.startsWith('/devices');
  const hasNoSpaceAccess = !isAccessLoading && !hasAnyAccess();
  const showMainboard = !!(spaceId || roomId || zoneId || buildingId || isDevicesRoute);

  return (
    <PopupContextProvider>
      <DetailsPanelProvider>
        <DashboardHeader />
        <PageLayoutRoot>
          <Sidebar />
          {location.pathname.startsWith('/settings') ? (
            <SettingsPage />
          ) : (
            <PageLayoutMain>
              <Header />
              {showMainboard && <Mainboard isRoom={!!roomId} isZone={!!zoneId} />}
              {hasNoSpaceAccess && !showMainboard && <NoSpacesAvailable />}
            </PageLayoutMain>
          )}
        </PageLayoutRoot>
      </DetailsPanelProvider>
    </PopupContextProvider>
  );
};

OrganizationPage.queries = {
  Floors: gql`
    query OrganizationPageFloors {
      floors {
        data {
          id: floor_id
          name
        }
      }
    }
  `,
};

export default memo(OrganizationPage);
